<template>
  <div
    class="wrapper"
    :class="{
      'wrapper--hide': modalOpen,
    }"
  >
    <TheHeader ref="header" />

    <SwipeActivityPrompt ref="swipeActivityPrompt" v-if="state === 'chapter'" />

    <div
      v-if="doTransition"
      class="content"
      :class="{
        'content--show': !menuOpen && !shortcutOpen,
      }"
    >
      <Transition>
        <Startup
          v-if="state === 'startup' && isLangReady"
          :authReady="authReady"
          :loggedIn="loggedIn"
          @next="afterStartup"
        />
        <OfflineStatus v-else-if="state === 'offline' && isLangReady" />
        <Splash
          v-else-if="state === 'splash'"
          ref="splash"
          @next="afterSplash"
        />
        <LoginWrapper
          v-else-if="state === 'login'"
          :guestId="guestId"
          @afterLogin="afterLogin"
          @changeLanguage="changeLanguage"
          @startGuestMode="startGuestMode"
        />
        <ProfileSelect
          v-else-if="state === 'profile'"
          @setProfile="setProfile"
          @logout="profileLogout"
        />
        <Intro v-else-if="state === 'intro'" @fin="toFirstConcept" />
        <FirstConcept
          v-else-if="state === 'first-concept'"
          @fin="toSafetyPlan"
        />
        <CreateSafetyPlan
          v-else-if="state === 'safety-plan'"
          @toInbox="toInbox"
          @toKeyConcepts="toKeyConcepts"
        />
        <Return v-else-if="state === 'return'" @next="toKeyConcepts" />
        <UpdateSuccess v-else-if="state === 'updated'" @next="toFork" />
        <PlayerFeedback v-else-if="state === 'feedback'" @next="toFork" />
        <Fork
          v-else-if="state === 'fork'"
          @toInbox="toInbox"
          @toKeyConcepts="toKeyConcepts"
        />
        <Inbox v-else-if="state === 'inbox'" @toChapter="toChapter" />
        <Chapter
          v-else-if="state === 'chapter'"
          ref="chapter"
          :chapter="chapter"
          @next="afterChapter"
          @toInbox="toInbox"
        />
        <KeyConcepts v-else-if="state === 'concepts'" @next="toFork" />
        <Outro v-else-if="state === 'outro'" @fin="toFork" />
      </Transition>

      <Shapes v-if="isAfterLogin" ref="shapes" />
    </div>

    <NavPrompt
      v-if="isControlsReady"
      ref="prompt"
      :menuOpen="menuOpen || shortcutOpen"
      :modalOpen="modalOpen"
    />

    <MenuIcon
      v-if="isControlsReady"
      ref="icon"
      :menuOpen="menuOpen"
      :pulse="menuPulse"
      :show="menuIconShow"
      @toggle="toggleMenu"
    />

    <ShortcutIcon
      v-if="isControlsReady"
      ref="shortcut"
      :shortcutOpen="shortcutOpen"
      :show="shortcutIconShow"
      @toggle="toggleShortcut"
    />

    <MuteButton v-if="isControlsReady" :show="isMuteButtonShown" />

    <MenuContainer
      :menuOpen="menuOpen"
      :shortcutOpen="shortcutOpen"
      :updateReady="updateReady"
    />
  </div>
</template>

<script>
import "@/assets/styles/main.scss";

import { Auth } from "@aws-amplify/auth";
import { API } from "@aws-amplify/api";

import * as AudioManager from "@/scripts/audioManager.js";
import * as Background from "@/scripts/background.js";
import * as Insert from "@/scripts/insert.js";
import * as Input from "@/scripts/input.js";
import * as Data from "@/scripts/data.js";
import * as ServiceWorker from "@/registerServiceWorker.js";
import * as Scroll from "@/scripts/scroll.js";
import PersistentData from "@/scripts/persistentData.js";
import UserData from "@/scripts/userData.js";
import UserApi from "@/scripts/userApi.js";
import OfflineMedia from "@/scripts/offlineMedia.js";
import SessionData from "@/scripts/sessionData.js";
import * as Connection from "@/scripts/connection.js";
import * as Analytics from "@/scripts/analytics.js";
import { defaultGradient } from "@/scripts/globals.js";
import {
  allChaptersComplete,
  testCompletionCount,
  firstIncompleteChapter,
} from "@/scripts/helpers.js";

import TheHeader from "@/components/header/TheHeader.vue";
import Shapes from "@/components/Shapes.vue";
import NavPrompt from "@/components/NavPrompt.vue";
import SwipeActivityPrompt from "@/components/SwipeActivityPrompt.vue";
import MenuIcon from "@/components/MenuIcon.vue";
import ShortcutIcon from "@/components/ShortcutIcon.vue";
import MenuContainer from "@/components/sections/menu/MenuContainer.vue";
import MuteButton from "@/components/MuteButton.vue";
import Startup from "@/components/sections/Startup.vue";
import OfflineStatus from "@/components/sections/OfflineStatus.vue";
import Splash from "@/components/sections/Splash.vue";
import LoginWrapper from "@/components/sections/login/LoginWrapper.vue";
import ProfileSelect from "@/components/sections/profiles/ProfileSelect.vue";
import Intro from "@/components/sections/Intro.vue";
import FirstConcept from "@/components/sections/FirstConcept.vue";
import CreateSafetyPlan from "@/components/sections/CreateSafetyPlan.vue";
import Return from "@/components/sections/Return.vue";
import UpdateSuccess from "@/components/sections/UpdateSuccess.vue";
import PlayerFeedback from "@/components/sections/PlayerFeedback.vue";
import Fork from "@/components/sections/Fork.vue";
import Inbox from "@/components/sections/Inbox.vue";
import Chapter from "@/components/sections/chapter/Chapter.vue";
import KeyConcepts from "@/components/sections/KeyConcepts.vue";
import Outro from "@/components/sections/Outro.vue";

const MENU_DELAY = 1000;
const GUEST_POLL_INTERVAL = 10000;

export default {
  components: {
    Shapes,
    MenuIcon,
    MenuContainer,
    ShortcutIcon,
    MuteButton,
    NavPrompt,
    SwipeActivityPrompt,
    Startup,
    OfflineStatus,
    Splash,
    LoginWrapper,
    ProfileSelect,
    Intro,
    FirstConcept,
    CreateSafetyPlan,
    Return,
    UpdateSuccess,
    PlayerFeedback,
    Fork,
    Inbox,
    Chapter,
    KeyConcepts,
    TheHeader,
    Outro,
  },
  data() {
    return {
      chapter: null,
      modalOpen: false,
      modalHideCallback: null,
      state: "startup",
      doTransition: true,

      isOnline: Connection.isOnline,
      updateReady: Data.hasUpdate || ServiceWorker.waiting,
      langReady: false,

      // menu
      menuOpen: false,
      menuIconPulse: false,
      menuIconShow: false,

      // shortcut
      shortcutOpen: false,
      shortcutIconShow: false,

      // auth
      guestId: null,
      guestStatus: "unknown",
      guestPollTimeout: null,
      authReady: false,
      loggedIn: false,
    };
  },
  computed: {
    isMuteButtonShown() {
      // show mute button after login and before menu is available
      return this.menuOpen || this.state === "login";
    },
    isControlsReady() {
      // global app controls available when app is interactive and language data is ready
      // MuteButton, NavPrompt, MenuIcon
      return (
        this.langReady &&
        !(
          this.state === "black" ||
          this.state === "startup" ||
          this.state === "offline" ||
          this.state === "splash"
        )
      );
    },
    isAfterLogin() {
      return !(
        this.state === "black" ||
        this.state === "startup" ||
        this.state === "offline" ||
        this.state === "splash" ||
        this.state === "login" ||
        this.state === "profile"
      );
    },
    isLangReady() {
      return this.langReady;
    },
    menuPulse() {
      console.log("pulse icon?", this.menuIconPulse, this.isOnline);
      return this.menuIconPulse && this.isOnline;
    },
  },
  methods: {
    onKey(e) {
      // ESC - close ui layers in sequence
      if (e.key === "Escape") {
        // close modal first
        if (this.modalOpen) {
          this.modalHide();

          // close menu second
        } else if (this.menuOpen) {
          this.menuOpen = false;
          AudioManager.play("ui-menu-close");
          Scroll.start();
        }
        Input.keyboardMode();
      }
    },

    shouldShowFeedback() {
      return !UserData.feedback && testCompletionCount(UserData.progress, 3);
    },

    resetInboxPosition() {
      const index = firstIncompleteChapter(
        Data.data.chapters,
        UserData.progress
      );
      if (!isNaN(index)) {
        UserData.setChapterIndex(index);
      }
    },

    // STATES

    async afterLogin(cognitoUser) {
      UserData.clear();
      const syncPromise = UserData.sync();

      this.setLoggedIn(cognitoUser);

      // hold black screen until sync
      this.state = "black";
      await syncPromise;

      // profiles - user must select profile
      if (SessionData.hasProfiles) {
        this.state = "profile";
      }

      // no profiles - user starts playing
      else {
        this.loadUserSave();
        this.resetInboxPosition();
      }
    },

    // PROFILES

    // profile selected
    setProfile(profile) {
      UserData.setActiveProfile(profile);

      UserData.loadSave();
      this.resetInboxPosition();

      if (this.shouldShowFeedback()) {
        this.state = "feedback";
        Background.setColors(defaultGradient);
      } else if (UserData.intro) {
        this.state = "return";
        Background.setColors(defaultGradient);
      } else {
        this.state = "intro";
      }
    },

    // exit profile via menu
    menuProfile(promise) {
      this.menuOpen = false;
      this.doTransition = false;
      this.state = "black";
      Background.fadeToBlack();

      this.menuTimeout = setTimeout(async () => {
        this.doTransition = true;
        Scroll.start();

        await promise;

        this.state = "profile";
      }, MENU_DELAY);
    },

    toFirstConcept() {
      this.state = "first-concept";
      Background.transitionDown();
    },
    toSafetyPlan() {
      this.state = "safety-plan";
      Background.transitionDown();
    },
    async toFork() {
      // get latest save data
      const syncPromise = UserData.sync();

      // animate out while waiting...
      Background.transitionDown();
      this.state = "color";
      this.setHeaderDisplay(false);

      await syncPromise;

      // enter new state
      this.state = "fork";
    },
    toKeyConcepts() {
      this.state = "concepts";
      Background.transitionDown();
    },
    async toInbox() {
      // get latest save data
      const syncPromise = UserData.sync();

      // animate while waiting...
      Background.transitionDown();
      this.state = "color";

      await syncPromise;

      // enter new state
      this.state = "inbox";
    },
    toChapter(chapter) {
      this.chapter = chapter;
      this.state = "chapter";
    },

    afterChapter() {
      // outro - if not seen and all chapters completed
      if (!UserData.outro && allChaptersComplete(UserData.progress)) {
        this.state = "outro";
        Analytics.trackEvent("all_chapters_completed");
        Background.setColors(defaultGradient);
        Background.transitionDown();
      }

      // feedback - if not seen and 3 chapters completed
      else if (this.shouldShowFeedback()) {
        this.state = "feedback";
        Background.setColors(defaultGradient);
        Background.transitionDown();
      }

      // inbox - default
      else {
        this.toInbox();
      }
    },

    // HEADER

    setHeaderDisplay(bool) {
      this.$refs.header.setDisplay(bool);
    },
    setHeaderTitle(title, reverse = false) {
      this.$refs.header.setTitle(title, reverse);
    },
    setHeaderProgress(progress, total, pattern) {
      this.$refs.header.setProgress(progress, total, pattern);
    },
    setHeaderProgressStyle(style) {
      this.$refs.header.setProgressStyle(style);
    },
    saveHeaderState() {
      this.$refs.header.saveState();
    },
    restoreHeaderState() {
      this.$refs.header.restoreState();
    },

    // SHAPES

    shapeState(state) {
      if (this.$refs.shapes) this.$refs.shapes.setState(state);
    },
    shapeTarget(element) {
      if (this.$refs.shapes) this.$refs.shapes.setTarget(element);
    },
    shapeIn() {
      if (this.$refs.shapes) this.$refs.shapes.in();
    },
    shapeOut() {
      if (this.$refs.shapes) this.$refs.shapes.out();
    },
    shapeNext(options) {
      if (this.$refs.shapes) this.$refs.shapes.next(options);
    },

    // PAGE NAV
    // the nav prompt needs to be able to trigger the page transition

    setPage(page) {
      this.page = page;
    },
    nextPage() {
      if (this.page) {
        this.page.toNextPage();
        this.page = null;
      }
    },

    // ICONS

    showAllIcons() {
      this.showMenuIcon();
      this.showShortcutIcon();
    },
    hideAllIcons() {
      this.hideMenuIcon();
      this.hideShortcutIcon();
    },

    // MENU ICON

    showMenuIcon() {
      this.menuIconShow = true;
    },

    hideMenuIcon() {
      this.menuIconShow = false;
    },

    menuIconStartPulse() {
      // only pulse if the menu is closed
      if (!this.menuOpen) {
        this.menuIconPulse = true;
      }
    },
    menuIconStopPulse() {
      this.menuIconPulse = false;
    },
    pulseIfUpdate() {
      if (this.updateReady) {
        this.menuIconStartPulse();
      }
    },

    // SHORTCUT ICON

    showShortcutIcon() {
      // only show if currently in a chapter
      if (this.state === "chapter") {
        this.shortcutIconShow = true;
      }
    },

    hideShortcutIcon() {
      this.shortcutIconShow = false;
    },

    // MENU

    toggleMenu() {
      if (this.menuOpen) {
        AudioManager.play("ui-menu-close");
        this.menuOpen = false;
        this.showAllIcons();
        Scroll.start();
      } else {
        this.menuOpen = true;
        this.hideShortcutIcon();
        Scroll.stop();
        this.menuIconStopPulse();
      }
    },

    // SHORTCUT

    toggleShortcut() {
      if (this.shortcutOpen) {
        AudioManager.play("ui-menu-close");
        this.shortcutOpen = false;
        Scroll.start();
      } else {
        this.shortcutOpen = true;
        Scroll.stop();
      }
    },

    goToShortcut(index) {
      this.shortcutOpen = false;
      Scroll.start();

      if (!this.$refs.chapter) return;
      this.$refs.chapter.menuChoice(index);
    },

    // prompt

    promptValue(v) {
      if (this.$refs.prompt) this.$refs.prompt.setValue(v);
    },
    promptShow() {
      if (this.$refs.prompt) this.$refs.prompt.show();
    },
    promptSetEnd(bool) {
      if (this.$refs.prompt) this.$refs.prompt.setEnd(bool);
    },
    promptReset() {
      if (this.$refs.prompt) this.$refs.prompt.reset();
    },

    // activity prompt

    showSwipePrompt() {
      if (this.$refs.swipeActivityPrompt) {
        this.$refs.swipeActivityPrompt.show();
      }
    },
    hideSwipePrompt() {
      if (this.$refs.swipeActivityPrompt) {
        this.$refs.swipeActivityPrompt.hide();
      }
    },

    // modal

    modalShow(callback) {
      this.modalOpen = true;
      this.modalHideCallback = callback;
    },
    modalHide() {
      this.modalOpen = false;
      if (this.modalHideCallback) {
        this.modalHideCallback();
        this.modalHideCallback = null;
      }
    },

    // GUEST MODE

    // pass code to manifest so it can be used in pwa
    async checkGuest() {
      clearTimeout(this.guestPollTimeout);

      // only check if status unknown
      if (this.guestStatus !== "unknown") return;

      // get the current guest code

      // prefer new code in url
      let guestCode = window.location.pathname.replaceAll("/", "");

      if (guestCode && guestCode !== PersistentData.guestCode) {
        PersistentData.setGuestCode(guestCode);
      } else {
        guestCode = PersistentData.guestCode;
      }

      // if no code in url or local data - return nulls
      if (!guestCode) {
        this.guestStatus = null;
        return;
      }

      // send code to api for validation
      try {
        const groupId = await API.get(
          "PublicAPI",
          "/app/check-guest?code=" + guestCode
        );

        // accepted
        this.guestStatus = "accepted";
        this.guestId = groupId;
      } catch (error) {
        // rejected
        if (error?.response?.status === 404) {
          this.guestStatus = "rejected";
          console.error("getGuest() code invalid", error);
        }

        // unknown
        else {
          this.guestStatus = "unknown";
          console.error("getGuest() unknown error", error);

          // try again
          this.guestPollTimeout = setTimeout(
            this.checkGuest,
            GUEST_POLL_INTERVAL
          );
        }
      }
    },
    startGuestMode(groupId) {
      UserData.setGuestMode(true);
      SessionData.setIsFacilitated(true);
      Analytics.trackEvent("play_as_guest", { group: groupId });
      Insert.setUsername(SessionData.lang.Login.guest_email_placeholder);
      this.state = "intro";
    },

    setShortcut(name, music, concept, level, state) {
      UserData.setNickname(name);
      UserData.setMusic(music);
      UserData.setSafetyPlan([
        {
          emoji: "👩‍🍼",
          label: "Parent",
          full: "Talk to my parent",
        },
        {
          emoji: "🧓🏽",
          label: "Grandparent",
          full: "Talk to my grandparent",
        },
        {
          emoji: "🏫",
          label: "School",
          full: "Go to my school",
        },
        {
          emoji: "👮‍♀️",
          label: "Police",
          full: "Call the police: 911",
        },
      ]);

      UserData.setIntro(true);
      UserData.setConcept(concept);
      UserData.setPlayerLevel(level);

      UserData.saveProgress();

      if (PersistentData.updating) {
        this.state = "splash";

        Background.fadeToBlack();
      } else {
        this.state = state;
        Background.setColors(defaultGradient);
      }
    },

    processHash() {
      console.log("App.processHash()");

      const hash = location.hash;

      // normal flow
      if (!hash) {
        console.log("normal load flow");
        this.toStartPoint();
        return;
      }

      if (!this.loggedIn) this.startGuestMode();

      // skip bootup
      if (hash === "#inbox") {
        console.log("Setting progress to inbox");
        for (const id in UserData.progress) {
          const chapterProgress = UserData.progress[id];

          chapterProgress.story = false;

          const activities = chapterProgress.activities;
          for (let i = 0; i < activities.length; ++i) {
            activities[i] = false;
          }
        }

        this.setShortcut("Tash", "Hip-hop", 1, 0, "return");
      }

      // skip bootup and set all chapters to completed
      else if (hash === "#finished") {
        console.log("Setting progress to finished");
        for (const id in UserData.progress) {
          const chapterProgress = UserData.progress[id];

          chapterProgress.story = true;

          const activities = chapterProgress.activities;
          for (let i = 0; i < activities.length; ++i) {
            activities[i] = true;
          }
        }

        this.setShortcut(
          "Tash",
          "Hip-hop",
          0,
          Data.data ? Data.data.chapters.length - 1 : 0,
          "return"
        );
      } else if (hash === "#feedback") {
        console.log("Setting progress to feedback");
        this.setShortcut(
          "Tash",
          "Hip-hop",
          0,
          Data.data ? Data.data.chapters.length - 1 : 0,
          "feedback"
        );
      }

      // complete up to N chapters
      else if (hash.indexOf("#chapter-") === 0) {
        const split = hash.split("-");
        console.log("Setting progress to chapter", split[1]);

        // get true order of chapters
        let chapterOrder = [];
        for (const group of PersistentData.data.chapters) {
          for (const chapter of group) {
            chapterOrder.push(chapter.post_id.toString());
          }
        }

        for (const id in UserData.progress) {
          const chapterProgress = UserData.progress[id];
          const chapterIndex = chapterOrder.indexOf(id);
          const complete = chapterIndex < split[1];

          chapterProgress.story = complete;

          const activities = chapterProgress.activities;
          for (let i = 0; i < activities.length; ++i) {
            activities[i] = complete;
          }
        }

        this.setShortcut("Tash", "Hip-hop", 1, 0, "return");
      }

      // skip to first concept
      else if (hash == "#first-concept") {
        this.setShortcut("Tash", "Hip-hop", 1, 0, "first-concept");
      }

      // skip to inbox and set the next key concept
      else if (hash.indexOf("#key-concept-") === 0) {
        let concept = 0;
        const split = hash.split("-");
        if (split[3]) {
          console.log(
            `Setting progress to key concept ${split[2]}.${split[3]}`
          );
          concept =
            (parseInt(split[3]) - 1) * Data.data.key_concepts.length +
            parseInt(split[2]) -
            1;
        } else {
          console.log("Setting progress to key concept", split[2]);
          concept = parseInt(split[2]) - 1;
        }

        this.setShortcut("Tash", "Hip-hop", concept, 0, "return");
      }
    },

    // CHOOSE START SCREEN

    // start point - after startup, after returning online
    toStartPoint() {
      // login - user not authenticated, need to log in
      if (!this.loggedIn) {
        this.state = "login";
        Background.fadeToBlack();
      }

      // profiles
      else if (SessionData.hasProfiles) {
        if (UserData.activeProfile) {
          this.loadProfileSave();
        } else {
          this.state = "profile";
        }
      }

      // non-profiles
      else {
        this.loadUserSave();
      }
    },
    loadUserSave() {
      UserData.loadSave();

      if (this.shouldShowFeedback()) {
        this.state = "feedback";
        Background.setColors(defaultGradient);
      } else if (UserData.intro) {
        this.state = "return";
        Background.setColors(defaultGradient);
      } else {
        this.state = "intro";
        Background.fadeToBlack();
      }
    },
    loadProfileSave() {
      UserData.loadSave();

      // catch case where profile deleted
      if (!UserData.currentSave) {
        this.state = "profile";
        Background.fadeToBlack();
      } else if (this.shouldShowFeedback()) {
        this.state = "feedback";
        Background.setColors(defaultGradient);
      } else if (UserData.intro) {
        this.state = "return";
        Background.setColors(defaultGradient);
      } else {
        this.state = "profile";
        Background.fadeToBlack();
      }
    },

    // STARTUP

    afterStartup() {
      Data.onUpdateNeeded(this.onUpdateNeeded); // force urgent update
      Data.onUpdateReady(this.onUpdateReady); // prompt user to update
      Data.onUpdateComplete(this.onUpdateComplete); // success!

      ServiceWorker.onWaiting(this.swWaiting); // new code downloaded
      ServiceWorker.onLoad(this.swLoad); // code ready - need reload

      // no offline mode for users without storage
      if (!OfflineMedia.available) {
        Connection.addOnlineEvent(this.offlineStart);
        Connection.addOfflineEvent(this.offlineEnd);

        if (Connection.isOffline) {
          this.state = "offline";
          return;
        }
      }

      this.processHash();
      this.pulseIfUpdate();
    },

    // ONLINE STATUS

    onOnline() {
      this.isOnline = true;
      this.checkGuest();
    },
    onOffline() {
      this.isOnline = false;
    },

    // OFFLINE + NO STORAGE
    // no storage means no media
    // show offline screen

    offlineStart() {
      this.state = "offline";
      Background.fadeToBlack();
      Background.transitionDown();
    },
    offlineEnd() {
      this.toStartPoint();
      this.pulseIfUpdate();
    },

    // MANUAL UPDATE
    // after startup

    // urgent updates are forced...
    onUpdateNeeded() {
      this.state = "splash";
    },

    // Data.js has new content ready
    onUpdateReady() {
      console.log("App.onUpdateReady()");

      // update now if urgent
      if (Data.needsUpdate || PersistentData.updating) {
        Data.update();
        this.state = "splash";
      }

      // prompt user to update
      else {
        this.setUpdateReady();
        Data.resume(); // continue polling for even newer content
      }
    },
    setUpdateReady() {
      this.updateReady = true;
      this.menuIconStartPulse();
    },

    //manually triggered from menu
    menuUpdate() {
      console.log("MANUAL UPDATE");

      this.menuOpen = false;
      this.doTransition = false;
      this.state = "black";

      // update service worker
      if (ServiceWorker.waiting) {
        Background.fadeToBlack();
        this.menuTimeout = setTimeout(ServiceWorker.update, MENU_DELAY);
      }

      // update content
      else {
        Background.setColors(defaultGradient);
        Background.dim();
        this.menuTimeout = setTimeout(() => {
          Data.update();
          this.state = "splash";
          this.doTransition = true;
          Scroll.start();
        }, MENU_DELAY);
      }
    },
    onUpdateComplete() {
      this.$refs.splash.fadeOut();
    },
    onLangReady() {
      this.langReady = true;
    },
    afterSplash() {
      // resume polling for updates
      this.updateReady = false;
      Data.resume();

      if (!this.loggedIn) {
        this.state = "login";
      } else {
        this.state = "updated";
        Background.setColors(defaultGradient);
      }

      Background.transitionDown();
    },

    // SERVICE WORKER UPDATE

    swWaiting() {
      console.log("App.swWaiting()");
      this.setUpdateReady();
      ServiceWorker.resume(); // accept even newer code
    },
    swLoad() {
      ServiceWorker.reload();
    },

    // LANGUAGE

    changeLanguage(newLanguage) {
      Data.changeLanguage(newLanguage);
      this.state = "splash";
    },

    // STRTUP AUTH

    async authFailed() {
      console.log("-- AUTH -- FAIL");
      this.loggedIn = false;
      this.authFinished();
    },
    async authSuccess(cognitoUser) {
      console.log("-- AUTH -- OKAY");
      this.setLoggedIn(cognitoUser);
      this.authFinished();
    },
    authFinished() {
      this.authReady = true; // let startup know user is authenticated

      // log out when lang changes
      // TODO - refactor me please
      Data.onLangReady(this.onLangReady);
      Data.onLangError(this.signout);
    },

    // LOGIN STATE

    setLoggedIn(cognitoUser) {
      console.log("App.setLoggedIn()", cognitoUser);
      this.loggedIn = true;

      Analytics.trackSession(cognitoUser);

      SessionData.setIsFacilitated(
        cognitoUser.attributes["custom:isFacilitated"] === "True"
      );
      SessionData.setHasProfiles(
        cognitoUser.attributes["custom:hasProfiles"] === "True"
      );
      Insert.setUsername(cognitoUser.attributes.email);

      UserApi.start();
    },

    // LOGOUT SEQUENCE

    async signout() {
      console.log("signout");

      if (this.loggedIn) {
        this.loggedIn = false;
        await UserApi.stop(); // await or abort pending cloud saves
        await Auth.signOut();
        Analytics.trackEvent("logout");
        Analytics.destorySession();
      }
    },
    menuLogout(promise) {
      this.menuOpen = false;
      this.doTransition = false;
      this.state = "black";
      Background.fadeToBlack();

      this.menuTimeout = setTimeout(async () => {
        this.doTransition = true;
        Scroll.start();

        await promise;

        this.state = "login";
      }, MENU_DELAY);
    },
    async profileLogout() {
      const signoutPromise = this.signout();

      this.hideAllIcons();
      Background.fadeToBlack();

      this.state = "black";

      await signoutPromise;

      this.state = "login";
    },
  },

  created() {
    Auth.currentAuthenticatedUser().then(this.authSuccess, this.authFailed);

    // monitor online status
    // - check guest when online
    // - inform startup sequence of online status
    Connection.addOnlineEvent(this.onOnline);
    Connection.addOfflineEvent(this.onOffline);

    if (Connection.isOnline) {
      this.checkGuest();
    }

    Background.initColors();
  },
  mounted() {
    window.addEventListener("keydown", this.onKey);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKey);

    clearTimeout(this.menuTimeout);
    clearTimeout(this.guestPollTimeout);
    cancelAnimationFrame(this.frameDelay);

    // stop modules
    // these will clear any callbacks internally
    Data.stop();
    Connection.stop();
    OfflineMedia.stop();
    UserApi.stop();
    ServiceWorker.stop();
  },
};
</script>

<style lang="scss">
// styles that need to be set AFTER all other styles
// TODO

// default space between content in pages
.page-content-margin {
  margin-top: $content-margin;

  &:first-child {
    margin-top: 0;
  }
}

.card-margin {
  margin: $content-radius 0 0;
}
</style>
